import { useEffect, useRef } from "react"
import { TOUR_STEPS } from "./tourSteps"
import { STATE_READY, useTour } from "./TourContext"

const MODAL_WIDTH = 480

export function TourTooltip() {
  const { currentStep, tourState, nextStep, goBack, skipTour, completeTour } = useTour()
  const modalRef = useRef(null)

  const stepDef = TOUR_STEPS.find(s => s.step === currentStep)
  const isLastStep = stepDef?.isLast || currentStep === TOUR_STEPS.length
  const isFirstStep = currentStep === 1
  const isReady = tourState === STATE_READY

  // Focus modal for keyboard accessibility when step becomes ready
  useEffect(() => {
    if (tourState === STATE_READY && modalRef.current) {
      modalRef.current.focus()
    }
  }, [tourState, currentStep])

  function handleKeyDown(e) {
    switch (e.key) {
      case "Escape":
        skipTour()
        break
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault()
        if (isReady) nextStep()
        break
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault()
        if (isReady && currentStep > 1) goBack()
        break
    }
  }

  if (!stepDef) return null

  return (
    <div
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-step-title"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 61,
        width: MODAL_WIDTH,
        maxWidth: "calc(100vw - 32px)",
      }}
      className="bg-white rounded-xl shadow-2xl p-6 outline-none"
    >
      {/* Step counter (hidden on welcome step) */}
      {!isFirstStep && (
        <p className="text-xs text-zinc-400 mb-3 font-medium">
          Step {currentStep} of {TOUR_STEPS.length}
        </p>
      )}

      <h2 id="tour-step-title" className="font-display text-lg font-semibold text-zinc-900 leading-snug">
        {stepDef.title}
      </h2>

      {stepDef.intro && (
        <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
          {stepDef.intro}
        </p>
      )}

      {stepDef.bullets && stepDef.bullets.length > 0 && (
        <ul className="mt-3 space-y-2.5">
          {stepDef.bullets.map((bullet, i) => (
            <li key={i} className="flex gap-2.5 text-sm text-zinc-600 leading-relaxed">
              <span className="text-kf-red font-bold shrink-0 mt-0.5">•</span>
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Buttons */}
      <div className="flex items-center justify-between mt-5 gap-2">
        {isFirstStep ? (
          // Welcome step: Skip Tour + Start Tour
          <div className="flex gap-2 ml-auto">
            <button
              onClick={skipTour}
              className="text-sm text-zinc-500 hover:text-zinc-700 px-3 py-1.5 rounded-lg transition-colors"
            >
              Skip Tour
            </button>
            <button
              onClick={nextStep}
              className="text-sm bg-kf-red text-white font-medium px-4 py-1.5 rounded-lg hover:bg-kf-red/90 transition-colors"
            >
              Start Tour →
            </button>
          </div>
        ) : (
          // Steps 2+: Back on left, Next/Finish on right
          <>
            <button
              onClick={goBack}
              disabled={!isReady}
              className="text-sm text-zinc-500 hover:text-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors px-2 py-1"
            >
              ← Back
            </button>

            <button
              onClick={isLastStep ? completeTour : nextStep}
              disabled={!isReady}
              className="text-sm bg-kf-red text-white font-medium px-4 py-1.5 rounded-lg hover:bg-kf-red/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLastStep ? "Finish Tour ✓" : "Next →"}
            </button>
          </>
        )}
      </div>

      {/* Skip link (hidden on welcome step where Skip Tour button is already prominent) */}
      {!isFirstStep && (
        <button
          onClick={skipTour}
          className="text-xs text-zinc-400 hover:text-zinc-600 mt-3 block w-full text-center transition-colors"
        >
          Skip tour
        </button>
      )}
    </div>
  )
}
